/**
 * Notifications queries (TanStack Query).
 *
 * Sprint 4 UI: sidebar badge + bildirim listesi.
 */

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

export type NotificationType =
  | 'OFFER_RECEIVED'
  | 'OFFER_AWARDED'
  | 'ORDER_CREATED'
  | 'ORDER_STATUS_CHANGED'
  | 'CHAT_MESSAGE'
  | 'SUBSCRIPTION_REQUEST'
  | 'SUBSCRIPTION_APPROVED'
  | 'SUBSCRIPTION_REJECTED'
  | 'LIST_EXPIRED'
  | 'LIST_UPDATED'
  | 'SUBSCRIPTION_EXPIRED'

export type NotificationItem = {
  id: string
  type: NotificationType
  title: string
  body: string
  link: string | null
  readAt: string | null
  createdAt: string
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (opts?: { unreadOnly?: boolean; limit?: number }) =>
    [...notificationKeys.all, 'list', opts ?? {}] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
}

/**
 * Current user'ın bildirimleri.
 */
export function useNotifications(opts?: { unreadOnly?: boolean; limit?: number }) {
  return useQuery({
    queryKey: notificationKeys.list(opts),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (opts?.unreadOnly) params.set('unreadOnly', 'true')
      if (opts?.limit) params.set('limit', String(opts.limit))
      const qs = params.toString()
      const res = await api<{
        ok: true
        data: { items: NotificationItem[]; total: number }
      }>(`/notifications${qs ? `?${qs}` : ''}`)
      return res.data
    },
  })
}

/**
 * Sidebar / Topbar badge için unread count (30s polling).
 */
export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: async () => {
      const res = await api<{ ok: true; data: { count: number } }>('/notifications/unread-count')
      return res.data.count
    },
    refetchInterval: 30_000,
  })
}

export function useNotificationStream() {
  const queryClient = useQueryClient()
  useEffect(() => {
    const stream = new EventSource('/api/notifications/stream', { withCredentials: true })
    const handleNotification = () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
      queryClient.invalidateQueries({ queryKey: ['messages', 'rooms'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-lists'] })
      queryClient.invalidateQueries({ queryKey: ['offer'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    }
    stream.addEventListener('connected', handleNotification)
    stream.addEventListener('notification', handleNotification)
    return () => {
      stream.removeEventListener('connected', handleNotification)
      stream.removeEventListener('notification', handleNotification)
      stream.close()
    }
  }, [queryClient])
}

/**
 * Tek bildirimi okundu işaretle.
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api<{ ok: true; data: { id: string; read: true } }>(
        `/notifications/${id}/read`,
        { method: 'PATCH' },
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/**
 * Tüm bildirimleri okundu işaretle.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api<{ ok: true; data: { marked: true } }>('/notifications/read-all', {
        method: 'POST',
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}
